'use client';

import { useEffect, useState } from 'react';
import { Modal, Col } from 'antd';
import { Button } from '@/components/ui';
import { useTranslations } from 'next-intl';
import { notifySessionExpired } from '@/lib/sessionExpired';
import Link from 'next/link';
import { useAppContext } from '@/app/core/AppContext';

/**
 * This component displays a modal when the user's session has expired.
 * It listens for a custom 'session-expired' event and overrides the global fetch
 * function to notify when a 401 status is encountered.
 *
 * The event is triggered by the API proxy whenever a 401 response is received (see @/lib/sessionExpired),
 * allowing us to show the modal immediately without waiting for the next user interaction.
 */
export function SessionExpiredModal() {
  const [visible, setVisible] = useState(false);
  const [returnPath] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));
  const t = useTranslations();
  const { me } = useAppContext();

  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener('session-expired', handler);

    const originalFetch = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const res = await originalFetch(...args);
      const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : '';
      if (res.status === 401 && url.startsWith('/api/proxy/')) {
        if (me) {
          notifySessionExpired();
        } else {
          // when `me` doesn't exist, we are redirected from a legacy page, so we shoudn't notify the user, but instead redirect to login page with returnUrl
          window.location.href = `/login?returnUrl=${encodeURIComponent(returnPath)}`;
        }
      }
      return res;
    };

    return () => {
      window.removeEventListener('session-expired', handler);
      window.fetch = originalFetch;
    };
  }, [me, returnPath]);

  return (
    <Modal open={visible} onCancel={() => setVisible(false)} mask={{ closable: false }} footer={null} centered>
      <h2>{t('SESSION_EXPIRED', { defaultMessage: 'Session expired' })}</h2>
      <p>{t('SESSION_EXPIRED_MESSAGE', { defaultMessage: 'Your session has expired. Please log in again to continue.' })}</p>
      <Col span={12} offset={12} className="top-20 text-right">
        <Link href={`/login?returnUrl=${encodeURIComponent(returnPath)}`} passHref>
          <Button className="btn" onClick={() => setVisible(false)}>
            {t('LOG_IN', { defaultMessage: 'Log in' })}
          </Button>
        </Link>
      </Col>
    </Modal>
  );
}
